(ns frontend.view.task.webentry-resolution.events
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.permissions.entity-permissions :as entity-permissions]
   [common.schema.associations :as associations]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.actions.deal :as deal-actions]
   [frontend.actions.entity :as entity-actions]
   [frontend.actions.task :as task-actions]
   [frontend.conn :as conn]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.current-settings :as current-settings]
   [frontend.state.permissions :as f-permissions]
   [frontend.updates.entity-scope :as entity-scope]
   [frontend.utils :as fu]
   [frontend.ux-overhaul.page-components.navigation.events :as navigation-events]
   [frontend.view.entity :as f-entity]
   [frontend.view.error-utils :as error-utils]
   [frontend.view.modals.account-select.view :as account-select]
   [frontend.view.modals.confirmation.view :as confirmation-modal]
   [frontend.view.modals.curtains.pass-curtain.view :as pass-curtain]
   [frontend.view.modals.curtains.put-on-hold-curtain.view :as put-on-hold-curtain]
   [frontend.view.modals.delete-confirmation.view :as delete-confirmation-modal]
   [frontend.view.modals.entity.attributes :as attributes]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.notification.view :as notification-modal]
   [potpuri.core :refer [fn-> index-by]]
   [promesa.core :as p]
   [reagent.core :as r]
   [reagent.ratom :as ratom]))

(defn get-entity
  [id]
  (get @session/entities id))

(defn resolved-ref-ids
  "The entity ids that `:task/resolved-data` points at: the resolved asset, the
   resolved deal, and each resolved contact. These ids live in a plain EDN blob,
   not as Datomic refs, so the task scope does not fetch them. A new-grid account
   does not bulk-sync the records either, so without a load they read back as
   missing and the summary views show a not-found placeholder."
  [resolved-data]
  (->> (concat [(get-in resolved-data [:asset :asset-id])
                (get-in resolved-data [:deal :deal-id])]
               (->> resolved-data :contacts vals (map :contact-id)))
       (filter int?)
       distinct
       vec))

(defn hydrate-missing-refs!
  "Load every id that the session does not have yet, so a new-grid account sees
   the resolved entity in the summary views. `load-entity!` also brings the
   entities that point at the loaded entity, so the deals that target a resolved
   asset arrive with it. Each id is loaded at most once, also on a failure, so a
   missing or unreadable id does not start a request storm."
  [requested* ids]
  (doseq [id ids]
    (when (and (int? id)
               (not (get @session/entities id))
               (not (contains? @requested* id)))
      (swap! requested* conj id)
      (entity-scope/load-entity!
       {:entity-id id
        :on-loaded (fn [_entity _entities] nil)
        :on-error  (fn [_error] nil)}))))

(defn resolved-refs-hydrator
  "Invisible tracker. It loads the entities that `:task/resolved-data` points at
   whenever that data changes, so a new-grid account sees the resolved asset,
   deal and contacts. It returns nil. For an old-grid account the entities are
   in the session already, so it loads nothing."
  [{:keys [resolved-data]}]
  (r/with-let [requested* (r/atom #{})]
    (hydrate-missing-refs! requested* (resolved-ref-ids @resolved-data))
    nil))

(defn- normalized-string
  [value]
  (some-> value str string/trim string/lower-case not-empty))

(defn matching-hit-id
  "The id of the first OpenSearch hit whose source map `match?` accepts, or nil.
   The hit id is a string in the response, so parse it. The text query can be
   loose, so `match?` keeps a wrong hit out."
  [hits match?]
  (some (fn [hit]
          (let [id (-> hit :_id js/parseInt)]
            (when (and (int? id)
                       (match? (-> hit :_source :original)))
              id)))
        hits))

(defn find-and-hydrate-match!
  "Run the same OpenSearch universal search that the new-grid pickers use, then
   hydrate the first hit that `match?` accepts into the session. The matching
   reactions (for example `email-lookup` and `asset-name-lookup`) read the
   session, so the hydrated entity becomes the match. `match?` takes the hit
   source map (`hit._source.original`) and returns a truthy value for an exact
   match."
  [{:keys [query entity-types match?]}]
  (-> (conn/sendp! :search/universal-search
                   {:query        query
                    :entity-types (vec entity-types)
                    :sort-terms   [{"extras.lower-case-display-name.keyword" {:order "asc"}}]
                    :filter-terms {}})
      (p/then
       (fn [{:keys [body]}]
         (when-let [matched (matching-hit-id (-> body :responses first :hits :hits)
                                             match?)]
           (entity-scope/hydrate! {:entity-id matched
                                   :on-done   (fn [] nil)}))))
      (p/catch
       (fn [error]
         (logging/error "Failed to search for a webentry match" error)))))

(defn webentry-matcher
  "Invisible tracker for a new-grid account. It searches OpenSearch for the
   submitted company name and each submitted contact email, and hydrates an
   exact match into the session, so the matching reactions offer the existing
   record instead of only a create form. It searches each distinct name and
   email once. An old-grid account has the records from the bulk sync, so this
   tracker does nothing. It returns nil."
  [{:keys [submitted-asset asset-type submitted-contacts]}]
  (r/with-let [searched* (r/atom #{})]
    (when (session/new-grid-2023?)
      (let [asset-type @asset-type
            asset-name (normalized-string (:asset/name @submitted-asset))
            emails (->> @submitted-contacts
                        (keep (fn-> :person/universal-email-address normalized-string))
                        distinct)]
        (when (and asset-name
                   (not (contains? @searched* [:asset asset-type asset-name])))
          (swap! searched* conj [:asset asset-type asset-name])
          (find-and-hydrate-match!
           {:query        asset-name
            :entity-types [asset-type]
            :match?       (fn [source]
                            (= asset-name (normalized-string (:asset/name source))))}))
        (doseq [email emails]
          (when-not (contains? @searched* [:person email])
            (swap! searched* conj [:person email])
            (find-and-hydrate-match!
             {:query        email
              :entity-types [:entity.type/person]
              :match?       (fn [source]
                              (= email (normalized-string
                                        (:person/universal-email-address source))))})))))
    nil))

(defn start-view
  [{:keys [task resolved-contacts-data submitted-contacts] :as _signals}]
  (let [task-status (:task/resolve-status @task)
        resolved-contacts-data @resolved-contacts-data
        submitted-contacts @submitted-contacts
        asset-resolved? (some #{task-status} [:task.webentry-status/asset-resolved])]
    (cond
      (and asset-resolved?
           (= (count resolved-contacts-data)
              (count submitted-contacts)))
      :deal
      asset-resolved?
      :contacts
      (some #{task-status}
            [:task.webentry-status/contacts-resolved
             :task.webentry-status/oppo-resolved])
      :deal
      :else
      :asset)))

(defn resolved-contact
  [{:keys [resolved-contacts-data]} person]
  (let [ord (:ord person)
        resolved-data @resolved-contacts-data
        resolved-id (get-in resolved-data [ord :contact-id])
        resolved-contact-entity (when (number? resolved-id)
                                  (get @session/entities resolved-id))]
    (when-not (or (nil? resolved-contact-entity)
                  (:common/disabled? resolved-contact-entity)
                  (not= (:entity/type resolved-contact-entity)
                        :entity.type/person))
      resolved-contact-entity)))

(defn matching-contact
  [{:keys [ord-contact-lookup]} person]
  (let [ord (:ord person)
        lookup @ord-contact-lookup
        match (get lookup ord)]
    match))

;;make vectors (cardinality/many) into sets
;; for proper equality comparison:
(defn vector-values-to-sets
  [entity]
  (->> entity
       (map (fn [[k v]]
              [k
               (if (vector? v)
                 (set v)
                 v)]))
       (into {})))

(defn deal-editor-type
  [attribute field-schema]
  (let [default-editor (->> field-schema
                            :field/default-visibility
                            :editor)
        data-type (:field/data-type field-schema)]
    (cond
      (= attribute :oppo/name)
      :editor.type/text-simple
      (= attribute :oppo/status)
      :editor.type/none
      (field/tag-field? attribute)
      :editor.type/multi-select
      (and (= default-editor :editor.type/none)
           (some #{data-type} [:data.type/ref-single :data.type/keyword]))
      :editor.type/select
      (and (= default-editor :editor.type/none)
           (some #{data-type} [:data.type/ref-multi :data.type/keyword-multi]))
      :editor.type/multi-select
      :else
      (->> field-schema
           :field/default-visibility
           :editor))))

(defn add-secondary-attributes
  [tenant-schema fields]
  (->> fields
       (mapcat (fn [f]
                 (if-let [sa (->> f
                                  (registry/get-field-schema tenant-schema)
                                  :field/secondary-attribute)]
                   [f sa]
                   [f])))))

(defn create-signals
  [state]
  (let [current-view (ratom/reaction
                      (-> @state
                          :current-view
                          (or :asset)))
        processing? (ratom/reaction
                     (:processing? @state))
        temp-state (r/cursor state [:temp-values])
        task-id (r/cursor state [:task-id])
        task (ratom/reaction
              (get-entity @task-id))
        no-task? (ratom/reaction
                  (let [task @task]
                    (or (nil? task)
                        (:common/disabled? task))))
        completed? (ratom/reaction
                    (:task/completed? @task))
        temp-task (r/cursor temp-state [@task-id])
        task-editable (ratom/reaction
                       (or @temp-task
                           @task))
        task-data (ratom/reaction
                   (:task/data @task))
        message (ratom/reaction
                 (:your-message-to-us @task-data))
        asset-type (ratom/reaction
                    (or (:webentry/asset-type @task-data)
                        :entity.type/company))
        all-teams session/all-teams
        available-webentry-asset-fields
        (ratom/reaction
         (let [tenant @session/tenant
               tenant-schema @session/tenant-schema
               tenant-configs (:tenant/field-configurations tenant)]
           (->> (entity-permissions/available-webentry-fields @asset-type
                                                              @all-teams
                                                              tenant-configs
                                                              tenant
                                                              tenant-schema)
                (add-secondary-attributes @session/tenant-schema)
                doall)))
        available-webentry-contact-fields
        (ratom/reaction
         (let [tenant @session/tenant
               tenant-schema @session/tenant-schema
               tenant-configs (:tenant/field-configurations tenant)]
           (->> (entity-permissions/available-webentry-fields :entity.type/person
                                                              @all-teams
                                                              tenant-configs
                                                              tenant
                                                              tenant-schema)
                (add-secondary-attributes @session/tenant-schema)
                doall)))
        available-webentry-deal-fields
        (ratom/reaction
         (let [tenant @session/tenant
               tenant-schema @session/tenant-schema
               tenant-configs (:tenant/field-configurations tenant)]
           (->> (entity-permissions/available-webentry-fields :entity.type/oppo
                                                              @all-teams
                                                              tenant-configs
                                                              tenant
                                                              tenant-schema)
                (add-secondary-attributes @session/tenant-schema)
                doall)))
        submitted-asset (ratom/reaction
                         (let [task-data @task-data
                               asset-type @asset-type
                               asset (get task-data asset-type)]
                           (vector-values-to-sets asset)))
        submitted-deal (ratom/reaction
                        (let [task-data @task-data
                              deal (get task-data :entity.type/oppo)]
                          (vector-values-to-sets deal)))
        attachments (ratom/reaction
                     (->> @task
                          :common/attachments
                          (keep get-entity)
                          (remove :common/disabled?)
                          (sort-by :common/created-on)
                          reverse
                          doall))
        resolved-data (ratom/reaction
                       (:task/resolved-data @task-editable))
        resolved-contacts-data (ratom/reaction
                                (:contacts @resolved-data))
        assets (ratom/reaction
                (session/get-entities @asset-type))
        persons session/all-persons
        deals session/all-oppos
        email-lookup
        (ratom/reaction
         (->> @persons
              (remove :common/disabled?)
              (remove (fn [person]
                        (string/blank? (:person/universal-email-address person))))
              (index-by (fn-> :person/universal-email-address string/trim string/lower-case))))
        submitted-contacts
        (ratom/reaction
         (->> @task-data
              :entity.type/person
              (map-indexed (fn [i person]
                             (let [person (assoc person :ord i :entity/type :entity.type/person)]
                               (vector-values-to-sets person))))
              vec
              doall))
        ord-contact-lookup
        (ratom/reaction
         (let [email-lookup @email-lookup
               resolved-data @resolved-contacts-data
               contacts @submitted-contacts]
           (->> contacts
                (keep (fn [{:keys [ord person/universal-email-address]}]
                        (let [email (->> universal-email-address
                                         str
                                         string/trim
                                         string/lower-case)
                              resolved-id (get-in resolved-data [ord :contact-id])
                              resolved-contact-entity (when (number? resolved-id)
                                                        (get @session/entities resolved-id))
                              matching-email-entity (when-not (string/blank? email)
                                                      (get email-lookup email))
                              resolved-contact-entity (if (or (nil? resolved-contact-entity)
                                                              (:common/disabled?
                                                               resolved-contact-entity))
                                                        matching-email-entity
                                                        resolved-contact-entity)]
                          (when resolved-contact-entity
                            [ord resolved-contact-entity]))))
                (into {}))))
        resolved-contacts
        (ratom/reaction
         (let [resolved-contacts-data @resolved-contacts-data
               contacts @submitted-contacts]
           (->> contacts
                (keep (fn [{:keys [ord]}]
                        (let [resolved-id (get-in resolved-contacts-data [ord :contact-id])
                              resolved-contact-entity (when (number? resolved-id)
                                                        (get @session/entities resolved-id))]
                          (when (and (not (nil? resolved-contact-entity))
                                     (not (:common/disabled? resolved-contact-entity))
                                     (= (:entity/type resolved-contact-entity)
                                        :entity.type/person))
                            resolved-contact-entity))))
                doall)))
        non-matching-contacts (ratom/reaction
                               (let [ord-contact-lookup @ord-contact-lookup]
                                 (->> @submitted-contacts
                                      (remove (fn [{:keys [ord]}]
                                                (contains? ord-contact-lookup ord)))
                                      doall)))
        matching-contacts (ratom/reaction
                           (let [ord-contact-lookup @ord-contact-lookup]
                             (->> @submitted-contacts
                                  (filter (fn [{:keys [ord]}]
                                            (contains? ord-contact-lookup ord)))
                                  doall)))
        ;;list of all contacts with extra info:
        contacts-list (ratom/reaction
                       (let [contacts @submitted-contacts
                             lookup @ord-contact-lookup]
                         (->> contacts
                              (map (fn [{:keys [ord] :as person}]
                                     (let [resolved (resolved-contact {:resolved-contacts-data
                                                                       resolved-contacts-data}
                                                                      person)
                                           match (get lookup ord)]
                                       (cond resolved
                                             (assoc person :resolved? true)
                                             match
                                             (assoc person :match? true)
                                             :else
                                             person))))
                              doall)))
        asset-name-lookup (ratom/reaction
                           (->> @assets
                                (remove :common/disabled?)
                                (index-by (fn-> :asset/name string/trim string/lower-case))))
        matching-asset (ratom/reaction
                        (when-let [asset-name (some-> @submitted-asset
                                                      :asset/name
                                                      str
                                                      string/lower-case)]
                          (get @asset-name-lookup asset-name)))
        matching-asset-attachments (ratom/reaction
                                    (some->> @matching-asset
                                             :common/attachments
                                             (map get-entity)
                                             (remove :common/disabled?)
                                             doall))
        resolved-asset-data (ratom/reaction
                             (:asset @resolved-data))
        resolved-asset (ratom/reaction
                        (let [resolved-asset-data @resolved-asset-data
                              resolved-id (:asset-id resolved-asset-data)]
                          (when resolved-id
                            (get-entity resolved-id))))
        resolved-deal-data (ratom/reaction
                            (:deal @resolved-data))
        resolved-deal (ratom/reaction
                       (let [resolved-deal-data @resolved-deal-data
                             resolved-id (:deal-id resolved-deal-data)]
                         (when resolved-id
                           (get-entity resolved-id))))
        resolved-asset-attachments (ratom/reaction
                                    (some->> @resolved-asset
                                             :common/attachments
                                             (map get-entity)
                                             (remove :common/disabled?)
                                             doall))
        resolved-deal-attachments (ratom/reaction
                                   (some->> @resolved-deal
                                            :common/attachments
                                            (map get-entity)
                                            (remove :common/disabled?)
                                            doall))
        existing-deals (ratom/reaction
                        (let [resolved-asset-id (:db/id @resolved-asset)]
                          (when resolved-asset-id
                            (->> @deals
                                 (filter (fn-> :oppo/target-asset (= resolved-asset-id)))
                                 (remove :common/disabled?)
                                 doall))))

        ;;indexed-by-deal-id: {deal-id [contact-associations-between-deal-and-its-target-asset]}
        deal-contact-associations
        (fn [contact]
          (let [contact-id (:db/id contact)
                resolved-asset-id (:db/id @resolved-asset)
                resolved-deal-data @resolved-deal-data
                resolved-deal-id (:deal-id resolved-deal-data)
                associations (fu/related-entity-associations :entity.type/oppo contact-id)
                deal-indexed
                (->> associations
                     (keep (fn [a]
                             (let [deal (fu/get-association-party a :entity.type/oppo)
                                   deal-id (:db/id deal)
                                   not-resolved-deal? (not= deal-id resolved-deal-id)
                                   target-asset-id (:oppo/target-asset deal)
                                   asset-associations (when (and target-asset-id
                                                                 (not= target-asset-id
                                                                       resolved-asset-id))
                                                        (fu/entities-associations
                                                         contact-id
                                                         target-asset-id))
                                   associations (into [a] asset-associations)]
                               (when (and not-resolved-deal?
                                          (seq associations))
                                 {deal-id associations}))))
                     (apply merge-with (comp distinct into))
                     doall)]
            deal-indexed))
        ;;{deal [matching-contact-associations-to-deal-or-its-target-asset]}
        deal-associations-lookup (ratom/reaction
                                  (when (session/dealflow?)
                                    (let [lookup @ord-contact-lookup
                                          contacts @matching-contacts]
                                      (doall
                                       (->> contacts
                                            (keep (fn [{:keys [ord]}]
                                                    (get lookup ord)))
                                            (keep deal-contact-associations)
                                            (apply merge-with into))))))
        deal-info-bar? (ratom/reaction
                        (or (not (string/blank? @message))
                            (seq @attachments)
                            (seq @deal-associations-lookup)))
        updater-state (r/cursor state [:updater])
        asset-updater-fields
        (ratom/reaction
         (let [asset-type @asset-type
               submitted-asset (dissoc @submitted-asset :asset/name)
               ;;matching-asset @matching-asset
               in-use (set @available-webentry-asset-fields)
               updater-fields
               (->> (keys submitted-asset)
                    (filter in-use)
                    ;;TODO potential improvement for later release for updater table:
                    ;;     show read-only fields as disabled with info tooltip:
                    (filter (fn [attribute]
                              (f-permissions/field-write-access?
                               attribute
                               asset-type
                               nil)))
                    (sort-by
                     (fn [a]
                       (let [fs (registry/get-field-schema @session/tenant-schema a)]
                         (loc/field-name a fs))))

                    ;;show/hide matching fields?
                    #_(remove (fn [k]
                                (= (get submitted-asset k)
                                   (get matching-asset k)))))]
           (doall updater-fields)))
        contact-updater-fields
        (ratom/reaction
         (let [submitted-contacts @submitted-contacts
               in-use (set @available-webentry-contact-fields)
               updater-fields (->> submitted-contacts
                                   (index-by :ord)
                                   (map (fn [[ord contact]]
                                          (let [_match (matching-contact
                                                        {:ord-contact-lookup ord-contact-lookup}
                                                        {:ord ord})]
                                            [ord
                                             (->> (keys contact)
                                                  (filter in-use)
                                                  (filter (fn [attribute]
                                                            (f-permissions/field-write-access?
                                                             attribute
                                                             :entity.type/person
                                                             nil)))
                                                  (remove #{:person/universal-email-address})
                                                  doall
                                                  ;;show/hide matching fields?
                                                  #_(remove (fn [k]
                                                              (= (get contact k)
                                                                 (get match k)))))])))
                                   (into {}))]
           updater-fields))
        contact-creator-fields (ratom/reaction
                                (let [fields attributes/person-create-attributes]
                                  fields))

        contact-creator-schema
        (ratom/reaction
         (let [schema (registry/get-schema @session/tenant-schema :entity.type/person)
               contact-creator-fields @contact-creator-fields
               creator-schema
               (->> schema
                    (filter (fn [[k _fs]]
                              (some #{k} contact-creator-fields)))
                    (filter (fn [[k _fs]]
                              (f-permissions/field-write-access? k :entity.type/person nil)))
                    (remove (fn [[_k fs]]
                              (-> fs
                                  :field/default-visibility
                                  :editor
                                  (= :editor.type/none))))
                    (map (fn [[k fs]]
                           [k
                            (merge fs
                                   {:attribute   k
                                    :access/type :field.access/write
                                    :editor/type (-> fs
                                                     :field/default-visibility
                                                     :editor)})]))
                    (into {}))]
           (->> contact-creator-fields
                (keep (fn [k]
                        (when-let [fs (get creator-schema k)]
                          [k fs])))
                doall)))
        deal-draft-state (r/cursor state [:deal])
        mandatory-deal-fields
        (ratom/reaction
         (let [fields (current-settings/mandatory-fields
                       {:entity-type              :entity.type/oppo
                        :state                    state
                        :default-mandatory-fields [:oppo/name]
                        :selected-pipeline-id     (or (:oppo/pipeline @resolved-deal)
                                                      (:oppo/pipeline @deal-draft-state))})
               ordered-fields [:oppo/name :oppo/pipeline :oppo/stage :oppo/lead-source-v2
                               :oppo/owner]]
           (concat ordered-fields
                   (->> fields
                        (remove (set ordered-fields))))))
        deal-creator-schema
        (ratom/reaction
         (let [schema (registry/get-schema @session/tenant-schema :entity.type/oppo)
               deal @submitted-deal
               mandatory-fields @mandatory-deal-fields
               visible-fields (distinct (concat mandatory-fields (keys deal)))
               in-use-fields (set (concat @available-webentry-deal-fields mandatory-fields))
               creator-schema (->> visible-fields
                                   (filter in-use-fields)
                                   (keep (fn [f]
                                           (when-let [fs (get schema f)]
                                             [f fs])))
                                   (filter (fn [[attribute _field-schema]]
                                             (let [allow? (f-permissions/field-write-access?
                                                           attribute
                                                           :entity.type/oppo
                                                           ;;handling selected pipeline from draft state:
                                                           (:oppo/pipeline @deal-draft-state))]
                                               allow?)))
                                   (map (fn [[attribute field-schema]]
                                          [attribute
                                           (merge field-schema
                                                  {:attribute   attribute
                                                   :access/type :field.access/write
                                                   :editor/type (deal-editor-type
                                                                 attribute
                                                                 field-schema)})]))
                                   (remove (fn-> second :editor/type (= :editor.type/none)))
                                   doall)]
           creator-schema))

        ;;asset-updater specific helper:
        discard? (ratom/reaction
                  (let [updater-values @updater-state
                        action (:action updater-values)]
                    (and (= :asset-updater-keep-current action)
                         (-> updater-values
                             :attachments
                             :selected
                             empty?))))
        webentry (ratom/reaction
                  (-> @task-data
                      :webentry-id
                      (get-entity)))
        notification (ratom/reaction
                      (:notification @state))
        ;; AI Suggestions - extracted from resolved-data
        ai-suggestions (ratom/reaction
                        (:ai-suggestions @resolved-data))
        has-ai-suggestions? (ratom/reaction
                             (boolean (or (seq (:companies @ai-suggestions))
                                          (seq (:contacts @ai-suggestions))
                                          (seq (:deals @ai-suggestions)))))
        ai-companies (ratom/reaction
                      (:companies @ai-suggestions))
        ai-contacts (ratom/reaction
                     (:contacts @ai-suggestions))
        ai-deals (ratom/reaction
                  (:deals @ai-suggestions))
        ai-pipeline-id (ratom/reaction
                        (:pipeline-id @ai-suggestions))
        ai-status (ratom/reaction
                   (:task/ai-processing-status @task))]
    {:state                             state
     :notification                      notification
     :processing?                       processing?
     :current-view                      current-view
     :temp-state                        temp-state
     :updater-state                     updater-state
     :task-id                           task-id
     :task                              task
     :no-task?                          no-task?
     :completed?                        completed?
     :task-data                         task-data
     :asset-type                        asset-type
     :assets                            assets
     :submitted-asset                   submitted-asset
     :submitted-contacts                submitted-contacts
     :non-matching-contacts             non-matching-contacts
     :resolved-contacts                 resolved-contacts
     :matching-contacts                 matching-contacts
     :contacts-list                     contacts-list
     :email-lookup                      email-lookup
     :ord-contact-lookup                ord-contact-lookup
     :submitted-deal                    submitted-deal
     :matching-asset                    matching-asset
     :message                           message
     :attachments                       attachments
     :matching-asset-attachments        matching-asset-attachments
     :resolved-data                     resolved-data
     :resolved-asset-data               resolved-asset-data
     :resolved-asset                    resolved-asset
     :resolved-asset-attachments        resolved-asset-attachments
     :resolved-deal-data                resolved-deal-data
     :resolved-deal                     resolved-deal
     :resolved-deal-attachments         resolved-deal-attachments
     :resolved-contacts-data            resolved-contacts-data
     :available-webentry-asset-fields   available-webentry-asset-fields
     :asset-updater-fields              asset-updater-fields
     :contact-updater-fields            contact-updater-fields
     :contact-creator-fields            contact-creator-fields
     :contact-creator-schema            contact-creator-schema
     :available-webentry-contact-fields available-webentry-contact-fields
     :available-webentry-deal-fields    available-webentry-deal-fields
     :discard?                          discard?
     :deal-associations-lookup          deal-associations-lookup
     :deal-info-bar?                    deal-info-bar?
     :existing-deals                    existing-deals
     :mandatory-deal-fields             mandatory-deal-fields
     :deal-creator-schema               deal-creator-schema
     :all-teams                         all-teams
     :webentry                          webentry
     ;; AI Suggestions
     :ai-suggestions                    ai-suggestions
     :has-ai-suggestions?               has-ai-suggestions?
     :ai-companies                      ai-companies
     :ai-contacts                       ai-contacts
     :ai-deals                          ai-deals
     :ai-pipeline-id                    ai-pipeline-id
     :ai-status                         ai-status}))

;; =============================================================================
;; AI Suggestion Helpers
;; =============================================================================

(defn get-ai-field-value
  "Extract value from AI field suggestion map.
   AI fields have structure: {:value x :confidence 0.9 :reasoning \"...\"}"
  [ai-fields field-key]
  (get-in ai-fields [field-key :value]))

(defn get-ai-field-confidence
  "Extract confidence score from AI field suggestion."
  [ai-fields field-key]
  (get-in ai-fields [field-key :confidence]))

(defn get-ai-field-reasoning
  "Extract reasoning from AI field suggestion."
  [ai-fields field-key]
  (get-in ai-fields [field-key :reasoning]))

(defn merge-ai-suggestions-into-draft
  "Merge AI suggested values into draft state.
   Only includes fields that have AI suggestions."
  [draft-state ai-fields]
  (reduce
   (fn [state [field-key {:keys [value]}]]
     (if (some? value)
       (assoc state field-key value)
       state))
   draft-state
   ai-fields))

(defn get-ai-company-for-asset
  "Get the first AI company suggestion (for asset creation).
   Returns nil if no suggestions."
  [{:keys [ai-companies]}]
  (first @ai-companies))

(defn get-ai-contact-by-index
  "Get AI contact suggestion by index."
  [{:keys [ai-contacts]} idx]
  (nth @ai-contacts idx nil))

(defn get-ai-deal
  "Get the first AI deal suggestion."
  [{:keys [ai-deals]}]
  (first @ai-deals))

;; =============================================================================
;; Notifications
;; =============================================================================

(defn hide-notification!
  [{:keys [state notification]}]
  (let [old-timeout (-> @notification
                        :timeout)]
    (when old-timeout
      (js/clearTimeout old-timeout))
    (swap! state update :notification dissoc :visible?)))

(defn show-notification!
  [{:keys [state notification] :as signals} {:keys [title text timeout]}]
  (let [old-timeout (-> @notification
                        :timeout)
        notification (merge {:visible? true
                             :title    title
                             :text     text}
                            (when timeout
                              {:timeout (fu/timeout
                                         timeout
                                         (partial hide-notification! signals))}))]
    (when old-timeout
      (js/clearTimeout old-timeout))
    (swap! state assoc :notification notification)))

(defn asset-view-info
  [{:keys [processing? matching-asset task resolved-data submitted-asset
           message attachments matching-asset-attachments]}]
  (let [matching-asset @matching-asset
        submitted-asset @submitted-asset
        processing? @processing?
        resolved-data @resolved-data
        resolve-status (:task/resolve-status @task)
        resolved-asset-data (get resolved-data :asset)
        {:keys [asset-id] asset-resolved-status :resolved-status} resolved-asset-data
        subview (cond
                  (empty? submitted-asset)
                  :no-asset-info
                  (and (nil? asset-id)
                       (or (nil? matching-asset)
                           (:common/disabled? matching-asset)))
                  :creator
                  asset-id
                  :summary
                  (and (nil? asset-id)
                       matching-asset
                       (not (:common/disabled? matching-asset)))
                  :updater
                  :else
                  :error)
        submitted-attachments @attachments
        matched-attachments @matching-asset-attachments
        message @message
        hide-info? (case subview
                     :summary true
                     :creator (and (empty? submitted-attachments)
                                   (string/blank? message))
                     :updater (and (empty? submitted-attachments)
                                   (empty? matched-attachments)
                                   (string/blank? message))
                     :no-asset-info (and (empty? submitted-attachments)
                                         (string/blank? message))
                     true)
        subview (if processing?
                  :processing
                  subview)
        show-skip? (and (nil? resolve-status)
                        (nil? asset-resolved-status))]
    {:subview    subview
     :show-info? (not hide-info?)
     :show-skip? show-skip?}))

(defn contacts-view-info
  [{:keys [resolved-asset submitted-contacts matching-contacts
           resolved-data message attachments]
    :as   signals}]
  (let [asset @resolved-asset
        asset-id (:db/id asset)
        resolved-data @resolved-data
        matching @matching-contacts
        submitted-attachments @attachments
        message @message
        hide-info? (and (empty? submitted-attachments)
                        (string/blank? message))
        resolved-contact-ords (->> resolved-data
                                   :contacts
                                   keys
                                   set)
        all-contacts-resolved? (->> @submitted-contacts
                                    (map :ord)
                                    (every? resolved-contact-ords))
        associations-missing? (and asset
                                   (->> matching
                                        (some (fn [person]
                                                (let [match (matching-contact signals person)]
                                                  (empty? (fu/entities-associations (:db/id match)
                                                                                    asset-id)))))))
        show-skip? (or (not all-contacts-resolved?)
                       associations-missing?)]
    {:show-info? (not hide-info?)
     :show-skip? show-skip?}))

(defn sort-mandatory-first
  [mandatory-fields schema]
  (let [mandatory-ords (->> mandatory-fields
                            (map-indexed (fn [i a]
                                           [a i]))
                            (into {}))]
    (->> schema
         (sort-by (fn [[a fs]]
                    (or (get mandatory-ords a)
                        (-> fs
                            :field/default-ord)))))))

(defn get-asset-summary-schema
  [{:keys [state available-webentry-asset-fields]} asset-type]
  (let [mandatory-fields (current-settings/mandatory-fields
                          {:entity-type              asset-type
                           :state                    state
                           :default-mandatory-fields [:asset/name]})
        available-webentry-asset-fields @available-webentry-asset-fields
        available-fields (set (concat mandatory-fields available-webentry-asset-fields))
        schema (registry/get-schema @session/tenant-schema asset-type)
        in-use-schema (->> schema
                           (filter (fn [[a _fs]]
                                     (contains? available-fields a)))
                           (remove (fn-> second :field/data-type (= :data.type/currency-symbol)))
                           (sort-mandatory-first mandatory-fields))]
    in-use-schema))

(defn get-deal-summary-schema
  [{:keys [state available-webentry-deal-fields resolved-deal]}]
  (let [available-webentry-deal-fields @available-webentry-deal-fields
        mandatory-fields (current-settings/mandatory-fields
                          {:entity-type              :entity.type/oppo
                           :state                    state
                           :default-mandatory-fields [:oppo/name]
                           :selected-pipeline-id     (:oppo/pipeline @resolved-deal)})
        mandatory-fields (concat [:oppo/name]
                                 (->> mandatory-fields
                                      (remove #{:oppo/name})))
        available-fields (set (concat mandatory-fields
                                      available-webentry-deal-fields))
        schema (registry/get-schema @session/tenant-schema :entity.type/oppo)
        in-use-schema (->> schema
                           (filter (fn [[a _fs]]
                                     (contains? available-fields a)))
                           (remove (fn-> second :field/data-type (= :data.type/currency-symbol)))
                           (sort-mandatory-first mandatory-fields))]
    in-use-schema))

(defn get-contact-summary-schema
  [{:keys [state available-webentry-contact-fields]}]
  (let [available-webentry-contact-fields @available-webentry-contact-fields
        mandatory-fields (current-settings/mandatory-fields
                          {:entity-type              :entity.type/person
                           :state                    state
                           :default-mandatory-fields [:person/universal-email-address]})
        available-fields (set (concat mandatory-fields
                                      available-webentry-contact-fields))
        schema (registry/get-schema @session/tenant-schema :entity.type/person)
        in-use-schema (->> schema
                           (filter (fn [[a _fs]]
                                     (contains? available-fields a)))
                           (remove (fn-> second :field/data-type (= :data.type/currency-symbol)))
                           (sort-mandatory-first mandatory-fields))]
    in-use-schema))

(defn go-back
  [{:keys [state current-view]}]
  (let [current-view @current-view
        prev-view (case current-view
                    :asset :asset
                    :contacts :asset
                    :deal :contacts
                    :asset)]
    (when-not (= prev-view current-view)
      (swap! state assoc :current-view prev-view))))

(defn continue
  [{:keys [state current-view]}]
  (let [current-view @current-view
        next-view (case current-view
                    :asset :contacts
                    :contacts :deal
                    :deal :deal                                                                     ;;<- go to tasks grid?
                    :asset)]
    (when-not (= next-view current-view)
      (swap! state assoc :current-view next-view))))

(defn skip-step
  [{:keys [_task-id current-view] :as signals}]
  (let [current-view @current-view
        _new-status (case current-view
                      :asset :task.webentry-status/asset-resolved
                      :contacts :task.webentry-status/contacts-resolved
                      nil)]
    (continue signals)
    ;;note: specs need clarifications on when to update resolve-status:
    #_(when new-status
        (entity-actions/*->field-update!
         @task-id
         :task/resolve-status
         new-status
         (fn []
           (continue signals))
         (fn [err]
           (logging/error "Failed to update task resolve status" err))))))

(defn in-use-asset-fields
  [{:keys [state asset-type available-webentry-asset-fields]}]
  (let [asset-type @asset-type
        mandatory-fields (current-settings/mandatory-fields
                          {:entity-type              asset-type
                           :state                    state
                           :default-mandatory-fields [:asset/name]})
        in-use-fields (->> (concat @available-webentry-asset-fields mandatory-fields)
                           (filter (fn [attribute]
                                     (f-permissions/field-write-access? attribute asset-type nil)))
                           set)]
    in-use-fields))

(defn create-asset!
  [{:keys [asset-type task state] :as signals} asset-state attachments-state]
  (let [attachments (:selected @attachments-state)
        entity-type @asset-type
        in-use-fields (in-use-asset-fields signals)
        asset (->> @asset-state
                   (filter (fn [[k _v]]
                             (contains? in-use-fields k)))
                   (into {}))
        asset (assoc asset
                     :entity/type
                     entity-type)
        attachments-write-access? (f-permissions/field-write-access?
                                   :common/attachments
                                   entity-type
                                   nil)
        asset (if (and attachments-write-access?
                       (seq attachments))
                (assoc asset :common/attachments attachments)
                asset)
        asset-name (:asset/name asset)
        on-success!
        (fn [res]
          (let [new-asset (:body res)
                new-asset-id (:db/id new-asset)
                task @task
                new-resolve-status :task.webentry-status/asset-resolved
                old-resolved-data (:task/resolved-data task)
                new-resolved-data (assoc old-resolved-data
                                         :asset
                                         {:resolved-status :created
                                          :asset-id        new-asset-id})
                new-data (merge {}
                                (when (not= (:task/resolve-status task)
                                            new-resolve-status)
                                  {:task/resolve-status new-resolve-status})
                                (when (not= old-resolved-data new-resolved-data)
                                  {:task/resolved-data new-resolved-data}))]
            (when-not (empty? new-data)
              (entity-actions/*->entity-update!
               (:db/id task)
               new-data
               (fn []
                 (let [asset-type-name (loc/tr entity-type)
                       type-lc (->> asset-type-name
                                    str
                                    string/lower-case)]
                   (fu/timeout
                    500
                    (fn []
                      (continue signals)
                      (show-notification! signals
                                          {:title   (loc/tr :resolved-asset-created-info-title
                                                            [asset-type-name asset-name])
                                           :text    (loc/tr :resolved-asset-created-info
                                                            [type-lc asset-name])
                                           :timeout 5000})
                      (swap! state dissoc :processing?)))))
               (fn [err]
                 (swap! state dissoc :processing?)
                 (logging/error "Failed to update task" err))))))]
    (swap! state assoc :processing? true)
    (entity-actions/*->create
     asset
     on-success!
     (fn [err]
       (swap! state dissoc :processing?)
       (modals/open-modal!
        notification-modal/modal-key
        {:body-text      (loc/tr :error-creating-asset)
         :title          (loc/tr :attention)
         :ok-button-text (loc/tr :ok)})
       (logging/error "Failed to create asset" err)))))

(defn select-empty!
  [{:keys [submitted-asset matching-asset updater-state] :as _signals}]
  (let [submitted-data (dissoc @submitted-asset :asset/name)
        matching-asset @matching-asset]
    (->> submitted-data
         cu/remove-blank-vals
         (filter (fn [[k _val]]
                   ;;if matching asset does not contain corresponding value, use submitted data
                   (cu/blank-value? (get matching-asset k))))
         (map first)
         set
         (swap! updater-state assoc :submitted-selected))))

(defn keep-current!
  [{:keys [updater-state]}]
  (swap! updater-state assoc :submitted-selected #{}))

(defn replace-all!
  [{:keys [submitted-asset updater-state asset-updater-fields]}]
  (let [submitted-data (dissoc @submitted-asset :asset/name)
        ;;only in-use-fields:
        fields (->> @asset-updater-fields
                    set)]
    (->> submitted-data
         cu/remove-blank-vals
         (map first)
         (filter fields)
         set
         (swap! updater-state assoc :submitted-selected))))

(defn select-update-action!
  [{:keys [updater-state] :as signals} kw]
  (swap! updater-state assoc :action kw)
  (case kw
    :asset-updater-update-empty
    (select-empty! signals)
    :asset-updater-keep-current
    (keep-current! signals)
    :asset-updater-replace-all
    (replace-all! signals)
    :asset-updater-customize
    nil))

;;
;; note: just replicating asset specific functionality for now - cleanup later if necessary
;;

(defn contact-select-empty!
  [{:keys [submitted-contacts] :as signals} updater-state ord]
  (let [submitted-contact (-> @submitted-contacts
                              (get ord)
                              (dissoc :person/universal-email-address :ord :match?))
        match (matching-contact signals {:ord ord})]
    (->> submitted-contact
         cu/remove-blank-vals
         (filter (fn [[k _val]]
                   ;;if matching asset does not contain corresponding value, use submitted data
                   (cu/blank-value? (get match k))))
         (map first)
         set
         (swap! updater-state assoc :submitted-selected))))

(defn contact-keep-current!
  [{:keys []} updater-state _ord]
  (swap! updater-state assoc :submitted-selected #{}))

(defn contact-replace-all!
  [{:keys [submitted-contacts contact-updater-fields]} updater-state ord]
  (let [submitted-contact (-> @submitted-contacts
                              (get ord)
                              (dissoc :person/universal-email-address :ord :match?))
        ;;only in-use-fields:
        fields (-> @contact-updater-fields
                   (get ord)
                   set)]
    (->> submitted-contact
         cu/remove-blank-vals
         (map first)
         (filter fields)
         set
         (swap! updater-state assoc :submitted-selected))))

(defn select-contact-update-action!
  [{:keys [] :as signals} updater-state ord kw]
  (swap! updater-state assoc :action kw)
  (case kw
    :updater-update-empty
    (contact-select-empty! signals updater-state ord)
    :updater-keep-current
    (contact-keep-current! signals updater-state ord)
    :updater-replace-all
    (contact-replace-all! signals updater-state ord)
    :updater-customize
    nil))

(defn select-submitted-contact-field!
  [{:keys [] :as _signals} updater-state _ord kw]
  (let [{:keys [submitted-selected]} @updater-state]
    (when-not (contains? (set submitted-selected) kw)
      (swap! updater-state update :submitted-selected (fnil conj #{}) kw)
      (swap! updater-state assoc :action :updater-customize))))

(defn select-current-contact-field!
  [{:keys [] :as _signals} updater-state _ord kw]
  (let [{:keys [submitted-selected]} @updater-state]
    (when (contains? (set submitted-selected) kw)
      (swap! updater-state update :submitted-selected disj kw)
      (swap! updater-state assoc :action :updater-customize))))

(defn select-submitted-asset-field!
  [{:keys [updater-state] :as _signals} kw]
  (let [{:keys [submitted-selected]} @updater-state]
    (when-not (contains? (set submitted-selected) kw)
      (swap! updater-state update :submitted-selected (fnil conj #{}) kw)
      (swap! updater-state assoc :action :asset-updater-customize))))

(defn select-current-asset-field!
  [{:keys [updater-state] :as _signals} kw]
  (let [{:keys [submitted-selected]} @updater-state]
    (when (contains? (set submitted-selected) kw)
      (swap! updater-state update :submitted-selected disj kw)
      (swap! updater-state assoc :action :asset-updater-customize))))

(defn update-asset!
  [{:keys [state asset-type submitted-asset matching-asset updater-state discard? task]
    :as   signals}]
  (let [matching-asset @matching-asset
        asset-type @asset-type
        submitted-asset @submitted-asset
        updater-values @updater-state
        new-info-keys (->> updater-values
                           :submitted-selected
                           (filter (fn [attribute]
                                     (f-permissions/field-write-access?
                                      attribute
                                      asset-type
                                      nil))))
        new-values (select-keys submitted-asset new-info-keys)
        new-values (->> new-values
                        (remove (fn [[k v]]
                                  (= (fu/if-bigdec-then-float v)
                                     (fu/if-bigdec-then-float (get matching-asset k)))))
                        (into {}))
        attachments-write-access? (f-permissions/field-write-access?
                                   :common/attachments
                                   asset-type
                                   nil)
        add-attachments (-> updater-values
                            :attachments
                            :selected
                            set)
        current-attachments (set (:common/attachments matching-asset))
        new-attachments (set/union add-attachments current-attachments)
        asset-id (:db/id matching-asset)
        msg (merge new-values
                   (when (and attachments-write-access?
                              (seq new-attachments)
                              (not= new-attachments current-attachments))
                     {:common/attachments new-attachments}))
        asset-name (or (:asset/name new-values)
                       (:asset/name matching-asset))
        discard? @discard?
        set-resolved-status? (or discard?
                                 (seq msg))
        on-success!
        (fn []
          (let [task @task
                new-resolve-status :task.webentry-status/asset-resolved

                asset-resolved-status (when set-resolved-status?
                                        (if discard?
                                          :discarded
                                          :updated))
                old-resolved-data (:task/resolved-data task)
                new-resolved-data (assoc old-resolved-data
                                         :asset
                                         (merge
                                          {:asset-id asset-id}
                                          (when set-resolved-status?
                                            {:resolved-status asset-resolved-status})))
                msg (merge {}
                           (when (not= (:task/resolve-status task)
                                       new-resolve-status)
                             {:task/resolve-status new-resolve-status})
                           (when (not= old-resolved-data new-resolved-data)
                             {:task/resolved-data new-resolved-data}))]
            (if-not (empty? msg)
              (entity-actions/*->entity-update!
               (:db/id task)
               msg
               (fn []
                 (let [asset-type-name (loc/tr asset-type)
                       title (case asset-resolved-status
                               :updated (loc/tr :resolved-asset-updated-info-title [asset-type-name])
                               :discarded (loc/tr :resolved-asset-discarded-info-title)
                               nil)
                       text (case asset-resolved-status
                              :updated (loc/tr :resolved-asset-updated-info [asset-name])
                              :discarded (loc/tr :resolved-asset-discarded-info [asset-name])
                              nil)]
                   (continue signals)
                   (swap! state dissoc :processing?)
                   (when (or title text)
                     (show-notification! signals
                                         {:title   title
                                          :text    text
                                          :timeout 5000}))))
               (fn [err]
                 (swap! state dissoc :processing?)
                 (logging/error "Failed to update task" err)))
              (do (continue signals)
                  (swap! state dissoc :processing?)))))]
    (swap! state assoc :processing? true)
    (if-not (empty? msg)
      (entity-actions/*->entity-update!
       asset-id
       msg
       on-success!
       (fn [err]
         (modals/open-modal!
          notification-modal/modal-key
          {:body-text      (loc/tr :error-updating-asset)
           :title          (loc/tr :attention)
           :ok-button-text (loc/tr :ok)})
         (logging/error "Failed to update asset" err)))
      (on-success!))))

(defn open-asset-creator!
  [{:keys [submitted-asset asset-type task updater-state] :as signals}]
  (let [asset-type @asset-type
        submitted-asset @submitted-asset
        initial-values (dissoc submitted-asset :asset/name)
        updater-values @updater-state
        add-attachments (-> updater-values
                            :attachments
                            :selected
                            set)
        attachments-write-access? (f-permissions/field-write-access?
                                   :common/attachments
                                   asset-type
                                   nil)
        initial-values (if (and attachments-write-access?
                                (seq add-attachments))
                         (assoc initial-values :common/attachments add-attachments)
                         initial-values)
        on-success!
        (fn [new-asset-id]
          (let [task @task
                new-resolve-status :task.webentry-status/asset-resolved
                old-resolved-data (:task/resolved-data task)
                new-resolved-data (assoc old-resolved-data
                                         :asset
                                         {:resolved-status :created
                                          :asset-id        new-asset-id})
                new-data (merge {}
                                (when (not= (:task/resolve-status task)
                                            new-resolve-status)
                                  {:task/resolve-status new-resolve-status})
                                (when (not= old-resolved-data new-resolved-data)
                                  {:task/resolved-data new-resolved-data}))]
            (when-not (empty? new-data)
              (entity-actions/*->entity-update!
               (:db/id task)
               new-data
               (fn []
                 (let [asset-type-name (loc/tr asset-type)
                       type-lc (->> asset-type-name
                                    str
                                    string/lower-case)
                       asset-name (->> new-asset-id
                                       (get @session/entities)
                                       :asset/name)]
                   (fu/timeout
                    500
                    (fn []
                      (continue signals)
                      (show-notification! signals
                                          {:title   (loc/tr :resolved-asset-created-info-title
                                                            [asset-type-name asset-name])
                                           :text    (loc/tr :resolved-asset-created-info
                                                            [type-lc asset-name])
                                           :timeout 5000})))))
               (fn [err]
                 (logging/error "Failed to update task" err))))))]
    (f-entity/open-creator!
     asset-type
     {:initial-values initial-values
      :on-ok!         (fn [{:keys [new-entity-id close-cb!]}]
                        (on-success! new-entity-id)
                        (close-cb!))})))

(defn create-person
  [{:keys [state task asset-type resolved-asset available-webentry-contact-fields] :as signals}
   draft-state association-type creator-modal?]
  (let [in-use @available-webentry-contact-fields
        in-use (->> in-use
                    (filter (fn [k]
                              (f-permissions/field-write-access? k :entity.type/person nil))))
        person @draft-state
        ord (:ord person)
        asset @resolved-asset
        person-state (r/cursor state [:persons ord])
        initial-values (cu/remove-blank-vals (select-keys person in-use))
        person-vals (assoc initial-values
                           :entity/type
                           :entity.type/person)
        on-success!
        (fn [new-person-id]
          (let [task @task
                old-resolved-data (:task/resolved-data task)
                new-resolved-data (assoc-in old-resolved-data
                                            [:contacts ord]
                                            {:resolved-status :created
                                             :contact-id      new-person-id})
                add-association? (and asset
                                      association-type)
                new-data (when (not= old-resolved-data new-resolved-data)
                           {:task/resolved-data new-resolved-data})
                update-person? (seq new-data)
                type-name (loc/tr :entity.type/person)
                type-lc (-> type-name
                            string/lower-case)
                done!
                (fn []
                  (let [person-name (entity/display-string (assoc person-vals :db/id "temp-id"))]
                    (show-notification! signals
                                        {:title   (loc/tr :resolved-entity-created-info-title
                                                          [type-name person-name])
                                         :text    (loc/tr :resolved-entity-created-info
                                                          [type-lc person-name])
                                         :timeout 5000})
                    (continue signals)
                    (swap! person-state dissoc :processing?)))]
            (when update-person?
              (entity-actions/*->entity-update!
               (:db/id task)
               new-data
               (fn []
                 (when-not add-association?
                   (fu/timeout
                    500
                    done!)))
               (fn [err]
                 (when-not add-association?
                   (swap! person-state dissoc :processing?))
                 (logging/error "Failed to update task" err))))

            (when add-association?
              (entity-actions/*->add-association
               new-person-id
               association-type
               @asset-type
               (:db/id asset)
               nil
               nil
               nil
               (fn [_]
                 (fu/timeout
                  500
                  done!))
               (fn [err]
                 (swap! person-state dissoc :processing?)
                 (logging/error "Failed to add association : " err))))
            ;;ensure processing? is set to nil:
            (when (and (not update-person?)
                       (not add-association?))
              (fu/timeout
               500
               done!))))]
    (swap! person-state assoc :processing? true)
    (if-not creator-modal?
      (entity-actions/*->create
       person-vals
       (fn [{:keys [body]}]
         (let [entity-id (:db/id body)]
           (on-success! entity-id)))
       (fn [err]
         (swap! person-state dissoc :processing?)
         (modals/open-modal!
          notification-modal/modal-key
          {:body-text      (loc/tr :error-creating-person)
           :title          (loc/tr :attention)
           :ok-button-text (loc/tr :ok)})
         (logging/error "Failed to create contact" err)))
      (f-entity/open-creator!
       :entity.type/person
       {:initial-values initial-values
        :on-cancel!     (fn []
                          (fu/timeout
                           200
                           (fn [] (swap! person-state dissoc :processing?))))
        :on-ok!         (fn [{:keys [new-entity-id close-cb!]}]
                          (on-success! new-entity-id)
                          (close-cb!))}))))

(defn update-contact!
  [{:keys [submitted-contacts task] :as signals} person-state ord]
  (let [updater-state (r/cursor person-state [:updater])
        submitted-contact (get @submitted-contacts ord)
        matching-contact (matching-contact signals {:ord ord})
        contact-id (:db/id matching-contact)
        updater-values @updater-state
        new-info-keys (->> updater-values
                           :submitted-selected
                           (filter (fn [attribute]
                                     (f-permissions/field-write-access?
                                      attribute
                                      :entity.type/person
                                      nil))))
        new-values (select-keys submitted-contact new-info-keys)
        new-values (->> new-values
                        (remove (fn [[k v]]
                                  (= (fu/if-bigdec-then-float v)
                                     (fu/if-bigdec-then-float (get matching-contact k)))))
                        (into {}))
        msg new-values
        on-success!
        (fn []
          (let [task @task
                old-resolved-data (:task/resolved-data task)
                new-resolved-data (assoc-in old-resolved-data
                                            [:contacts ord]
                                            {:resolved-status (if (empty? msg)
                                                                :resolved
                                                                :updated)
                                             :contact-id      contact-id})
                new-data (merge {}
                                (when (not= old-resolved-data new-resolved-data)
                                  {:task/resolved-data new-resolved-data}))
                type-name (loc/tr :entity.type/person)
                type-lc (-> type-name
                            string/lower-case)
                person-vals (merge matching-contact new-values)
                done!
                (fn []
                  (let [person-name (entity/display-string (assoc person-vals :db/id "temp-id"))]
                    (show-notification! signals
                                        {:title   (loc/tr :resolved-entity-updated-info-title
                                                          [type-name person-name])
                                         :text    (loc/tr :resolved-entity-updated-info
                                                          [type-lc person-name])
                                         :timeout 5000})
                    (continue signals)
                    (swap! person-state dissoc :processing?)))]
            (if-not (empty? new-data)
              (entity-actions/*->entity-update!
               (:db/id task)
               new-data
               done!
               (fn [err]
                 (logging/error "Failed to update task" err)))
              (done!))))]
    (swap! person-state assoc :update-processing? true)
    (if-not (empty? msg)
      (entity-actions/*->entity-update!
       contact-id
       msg
       on-success!
       (fn [err]
         (swap! person-state dissoc :update-processing?)
         (modals/open-modal!
          notification-modal/modal-key
          {:body-text      (loc/tr :error-updating-contact)
           :title          (loc/tr :attention)
           :ok-button-text (loc/tr :ok)})
         (logging/error "Failed to update contact" err)))
      (on-success!))))

(defn association-options
  [{:keys [asset-type]}]
  (let [asset-type @asset-type
        association-options (some->> associations/schema
                                     (cu/flip get :entity.type/person)
                                     (cu/flip get asset-type)
                                     (map (fn [[a-t _c]]
                                            {:key   a-t
                                             :label (loc/tr a-t)})))]
    association-options))

(defn update-association!
  [_signals association-id new-type clear-cb!]
  (when (keyword? new-type)
    (entity-actions/*->field-update!
     association-id
     :entity-association/type
     new-type
     (fn []
       (when (fn? clear-cb!)
         (fu/timeout 500 clear-cb!)))
     (fn [err]
       (modals/open-modal!
        notification-modal/modal-key
        {:body-text      (loc/tr :error-updating-asset)
         :title          (loc/tr :attention)
         :ok-button-text (loc/tr :ok)})
       (when (fn? clear-cb!)
         (fu/timeout 500 clear-cb!))
       (logging/error "Failed to update association" err)))))

(defn create-asset-association!
  [{:keys [asset-type resolved-asset] :as _signals} person-state person-id association-type
   clear-cb!]
  (let [asset-type @asset-type
        asset @resolved-asset]
    (swap! person-state assoc :association-processing? true)
    (entity-actions/*->add-association
     person-id
     association-type
     asset-type
     (:db/id asset)
     nil
     nil
     nil
     (fn [_]
       (fu/timeout 500
                   (fn []
                     (swap! person-state dissoc :association-processing?)
                     (when (fn? clear-cb!)
                       (clear-cb!)))))
     (fn [err]
       (swap! person-state dissoc :association-processing?)
       (fu/timeout 500 clear-cb!)
       (logging/error "Failed to add association : " err)))))

(defn reactivate!
  [deal]
  (let [deal-name (entity/display-string deal)
        resurrect-deal! (fn []
                          (deal-actions/*->resurrect
                           {:oppo-id (:db/id deal)}
                           (fn [])
                           (fn [err]
                             (logging/error "Failed to reactiate deal: " err))))]
    (modals/open-modal!
     confirmation-modal/modal-key
     {:body           [:div.text-center
                       [:p.mb-0 (loc/tr :you-are-reactivating [deal-name])]
                       [:p (loc/tr :reactivate-confirm-info)]
                       [:p.pt-3 (loc/tr :do-you-want-to-proceed)]]
      :confirm-text   (loc/tr :yes-reactivate)
      :confirm-icon   ""
      :cancel-text    (loc/tr :no-cancel)
      :title          (loc/tr :reactivate-a-deal)
      :skip-checkbox? true
      :on-confirm     resurrect-deal!})))

(defn start-pass!
  [oppo-id]
  (when (session/is-mobile?)
    (.scrollTo js/window 0 0))
  (modals/open-modal!
   pass-curtain/modal-key
   {:oppo-id oppo-id}))

(defn start-put-on-hold!
  [oppo-id]
  (when (session/is-mobile?)
    (.scrollTo js/window 0 0))
  (modals/open-modal!
   put-on-hold-curtain/modal-key
   {:oppo-id oppo-id}))

(defn create-deal!
  [{:keys [resolved-asset resolved-contacts task state] :as _signals} deal-state attachments-state
   action]
  (let [attachments (:selected @attachments-state)
        entity-type :entity.type/oppo
        deal @deal-state
        deal (->> deal
                  (filter (fn [[k _]]
                            (f-permissions/field-write-access?
                             k
                             :entity.type/oppo
                             (:oppo/pipeline deal))))
                  (into {}))
        deal (assoc deal
                    :entity/type
                    entity-type)
        attachments-write-access? (f-permissions/field-write-access?
                                   :common/attachments
                                   :entity.type/oppo
                                   (:oppo/pipeline deal))
        deal (if (and attachments-write-access?
                      (seq attachments))
               (assoc deal :common/attachments attachments)
               deal)
        target-asset-id (:db/id @resolved-asset)
        deal (if target-asset-id
               (assoc deal :oppo/target-asset target-asset-id)
               deal)
        on-success!
        (fn [res]
          (let [new-deal (:body res)
                new-deal-id (:db/id new-deal)
                resolved-contact-id (->> @resolved-contacts
                                         first
                                         :db/id)
                add-association? (boolean (number? resolved-contact-id))
                task @task
                new-resolve-status :task.webentry-status/oppo-resolved
                old-resolved-data (:task/resolved-data task)
                new-resolved-data (assoc old-resolved-data
                                         :deal
                                         {:resolved-status :created
                                          :deal-id         new-deal-id})
                new-data (merge {}
                                (when (not= (:task/resolve-status task)
                                            new-resolve-status)
                                  {:task/resolve-status new-resolve-status})
                                (when (not= old-resolved-data new-resolved-data)
                                  {:task/resolved-data new-resolved-data}))
                update-task-data? (seq new-data)
                further-action? (some #{action} [:pass :put-on-hold])
                only-task-update? (and (not further-action?)
                                       (not add-association?))
                add-deal-association! (fn []
                                        (entity-actions/*->add-association
                                         resolved-contact-id
                                         :association.type/contact-person
                                         :entity.type/oppo
                                         new-deal-id
                                         nil
                                         nil
                                         nil
                                         (fn [{new-association-id :body :as _body}]
                                           (when (and (= action :pass)
                                                      (number? new-association-id))
                                             (session/wait-for-entities-sync!
                                              [new-deal-id new-association-id]
                                              (fn []
                                                (start-pass! new-deal-id))))
                                           (when (and (= action :put-on-hold)
                                                      (number? new-association-id))
                                             (session/wait-for-entities-sync!
                                              [new-deal-id new-association-id]
                                              (fn []
                                                (start-put-on-hold! new-deal-id))))
                                           (fu/timeout
                                            500
                                            (fn []
                                              (swap! state dissoc :processing?))))
                                         (fn [err]
                                           (swap! state dissoc :processing?)
                                           (logging/error "Failed to add association : " err))))]
            (when update-task-data?
              (entity-actions/*->entity-update!
               (:db/id task)
               new-data
               (fn []
                 (when add-association?
                   (add-deal-association!))
                 (when only-task-update?
                   (swap! state dissoc :processing?)))
               (fn [err]
                 (when only-task-update?
                   (swap! state dissoc :processing?))
                 (logging/error "Failed to update task" err))))

            (when (and add-association?
                       (not update-task-data?))
              (add-deal-association!))

            (when (and (= action :pass)
                       (not add-association?))
              (session/wait-for-entity-sync!
               {:new-entity-id new-deal-id
                :new-entity    new-deal
                :close-cb!     (fn []
                                 (swap! state dissoc :processing?)
                                 (start-pass! new-deal-id))}))
            (when (and (= action :put-on-hold)
                       (not add-association?))
              (session/wait-for-entity-sync!
               {:new-entity-id new-deal-id
                :new-entity    new-deal
                :close-cb!     (fn []
                                 (swap! state dissoc :processing?)
                                 (start-put-on-hold! new-deal-id))}))))]
    (swap! state assoc :processing? true)
    (entity-actions/*->create
     deal
     on-success!
     (fn [err]
       (modals/open-modal!
        notification-modal/modal-key
        {:body-text      (loc/tr :error-creating-deal-try-another-name)
         :title          (loc/tr :attention)
         :ok-button-text (loc/tr :ok)})
       (logging/error "Failed to create deal" err)))))

(defn disable-contact!
  [_signals person]
  (let [on-error! (fn [_err]
                    (modals/open-modal!
                     notification-modal/modal-key
                     {:body-text      (loc/tr :contact-cannot-be-deleted
                                              [(entity/display-string person)])
                      :title          (loc/tr :attention)
                      :ok-button-text (loc/tr :ok)}))
        delete! (fn []
                  (entity-actions/*->disable-raw!
                   {:entity-id (:db/id person)
                    ;;should we pass this?
                    #_:prevent-if-associations?
                    #_true}
                   (fn [])
                   on-error!))]
    (modals/open-modal!
     delete-confirmation-modal/modal-key
     {:body       (loc/tr :delete-contact [(entity/display-string person)])
      :on-delete! (fn [_]
                    (delete!))})))

(defn complete-task!
  [{:keys [task] :as _signals}]
  (task-actions/*complete-task->
   {:db/id (:db/id @task)}
   (fn []
     (navigation-events/refresh-task-badge-counts!)
     (routing/set-view! [:tasks-grid]))
   (fn [err]
     (logging/error "Failed to complete task" err))))

(defn reactivate-task!
  [{:keys [task] :as _signals}]
  (task-actions/*reactivate-task->
   {:db/id (:db/id @task)}
   (fn [_resp]
     (navigation-events/refresh-task-badge-counts!))
   (fn [err]
     (logging/error "Failed to reactivate task" err))))

(defn initial-pipeline
  [{:keys [asset-type]}]
  (let [asset-type @asset-type
        pipeline (->> @session/selected-oppo-pipeline
                      (get @session/entities))
        pipeline (if (some #{asset-type} (:pipeline/target-asset-types pipeline))
                   pipeline
                   (->> @session/all-pipelines
                        (filter (fn [p]
                                  (some #{asset-type} (:pipeline/target-asset-types p))))
                        first))]
    (:db/id pipeline)))

(defn first-stage
  [pipeline-id]
  (->> (session/pipeline-stages pipeline-id)
       (sort-by :common/ord)
       (first)
       :db/id))

(defn pipeline-stages
  [pipeline-id]
  (->> (session/pipeline-stages pipeline-id)
       (sort-by :common/ord)))

(defn initial-deal-values
  [{:keys [task resolved-asset submitted-asset submitted-deal
           deal-creator-schema webentry ai-deals]
    :as   signals}]
  (let [default-name (or (:asset/name @resolved-asset)
                         (:asset/name @submitted-asset))
        default-source-category-id (->> (session/named-values :oppo/lead-source-v2)
                                        (cu/filter-by :named-value/value "Walk-in")
                                        first
                                        :db/id)
        pipeline-id (initial-pipeline signals)
        task @task
        webentry @webentry
        owner-id (->> [(:task/assignee task) (:webentry/handler-account webentry)
                       (:db/id @session/account)]
                      (filter session/active-account-id)
                      (first))
        stage-id (first-stage pipeline-id)
        ;; Get AI deal suggestion if available
        ai-deal (first @ai-deals)
        ai-fields (:fields ai-deal)
        base-values (merge {:oppo/name  default-name
                            :oppo/owner owner-id}
                           (when default-source-category-id
                             {:oppo/lead-source-v2 default-source-category-id})
                           (select-keys @submitted-deal
                                        (map first @deal-creator-schema))
                           {:oppo/status   :oppo.status/ok
                            :oppo/pipeline pipeline-id
                            :oppo/stage    stage-id})]
    ;; Merge AI suggestions into deal values
    (if ai-fields
      (merge-ai-suggestions-into-draft base-values ai-fields)
      base-values)))

(defn delete-task!
  [task]
  (entity-actions/*->disable! (:db/id task)
                              (fn [_resp]
                                (navigation-events/refresh-task-badge-counts!)
                                (routing/set-view! [:tasks-grid]))
                              error-utils/show-error!))

(defn start-delete-task!
  [task]
  (modals/open-modal!
   delete-confirmation-modal/modal-key
   {:body       (loc/tr :confirm-delete-task)
    :on-delete! (fn [_]
                  (delete-task! task))}))

(defn start-reassign-task!
  [task]
  (modals/open-modal!
   account-select/modal-key
   {:active-accounts (session/tenant-users)
    :header-text     (loc/tr :select-user)
    :on-select!      (fn [on-close! selected-account-id]
                       ;;optimistic update:
                       (swap! session/entities assoc-in
                              [(:db/id task) :task/assignee]
                              selected-account-id)
                       (entity-actions/*->field-update!
                        (:db/id task)
                        :task/assignee
                        selected-account-id
                        (fn [_]
                          ;; Reassigning to or away from the signed-in account
                          ;; changes their own unfinished count.
                          (navigation-events/refresh-task-badge-counts!)
                          (on-close!)
                          (routing/set-view! [:tasks-grid]))
                        (fn [err]
                          (let [entity (get @session/entities (:db/id task))
                                assignee (:task/assignee entity)]
                            ;;retract optimistic update:
                            (when (= assignee selected-account-id)
                              (swap! session/entities assoc-in
                                     [(:db/id task) :task/assignee]
                                     (:task/assignee task)))
                            (logging/log "Error assigning task : " err)))))}))
